import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Trash2,
  ChevronLeft,
  ChevronRight,
  Search,
  MessageSquareMore,
} from "lucide-react";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-hot-toast";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;
const PER_PAGE = 6;

function Comments() {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchComments = async () => {
      try {
        const { data } = await axios.get(`${SERVER_URL}/api/blogs/comments`);
        setComments(data);
      } catch {
        toast.error("Couldn't load comments. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchComments();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await axios.delete(`${SERVER_URL}/api/blogs/comments/${id}`);
      setComments((prev) => prev.filter((c) => c._id !== id));
      toast.success("Comment deleted");
    } catch {
      toast.error("Couldn't delete comment. Please try again.");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected comment(s)?`))
      return;
    try {
      await Promise.all(
        selectedIds.map((id) =>
          axios.delete(`${SERVER_URL}/api/blogs/comments/${id}`),
        ),
      );
      setComments((prev) => prev.filter((c) => !selectedIds.includes(c._id)));
      setSelectedIds([]);
      toast.success("Comments deleted");
    } catch {
      toast.error("Couldn't delete selected comments. Please try again.");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const filteredComments = useMemo(() => {
    return comments.filter(
      (c) =>
        c.name.toLowerCase().includes(query.toLowerCase()) ||
        c.text.toLowerCase().includes(query.toLowerCase()) ||
        c.blogId?.title?.toLowerCase().includes(query.toLowerCase()),
    );
  }, [comments, query]);

  const maxPage = Math.max(1, Math.ceil(filteredComments.length / PER_PAGE));
  const paginated = filteredComments.slice(
    (page - 1) * PER_PAGE,
    page * PER_PAGE,
  );
  const allSelectedOnPage = paginated.every((c) => selectedIds.includes(c._id));

  return (
    <div className="w-full max-w-7xl mx-auto space-y-8">
      {/* Page Header + Search */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">
            Blog Comments
          </h1>
          <p className="text-gray-500 mt-1">
            Moderate and manage reader comments
          </p>
        </div>
        <label className="relative w-full sm:w-80">
          <Search className="absolute left-3.5 top-3 text-gray-400" size={18} />
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Search by name, blog, or text..."
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition shadow-sm"
          />
        </label>
      </div>

      {/* Select All + Bulk Delete bar */}
      {paginated.length > 0 && (
        <div className="flex items-center justify-between bg-white border border-gray-100 rounded-2xl px-5 py-3 shadow-sm">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={allSelectedOnPage}
              onChange={(e) => {
                const ids = paginated.map((c) => c._id);
                if (e.target.checked) {
                  setSelectedIds((prev) => [...new Set([...prev, ...ids])]);
                } else {
                  setSelectedIds((prev) =>
                    prev.filter((id) => !ids.includes(id)),
                  );
                }
              }}
              className="w-4 h-4 accent-yellow-500 rounded"
            />
            <span className="text-sm font-medium text-gray-600">
              Select All on Page
            </span>
          </label>

          {selectedIds.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded-xl hover:bg-red-600 transition text-sm font-semibold"
            >
              <Trash2 size={14} /> Delete {selectedIds.length} Selected
            </button>
          )}
        </div>
      )}

      {/* Comments */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <CircularProgress size={35} sx={{ color: "#eab308" }} />
        </div>
      ) : filteredComments.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-[2rem] flex flex-col items-center justify-center h-64 gap-3">
          <MessageSquareMore className="w-12 h-12 text-gray-200" />
          <p className="text-gray-400 font-bold text-sm tracking-tight">
            No discussions found
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {paginated.map((comment) => (
              <div
                key={comment._id}
                className={`bg-white border rounded-2xl p-4 shadow-sm hover:shadow-md transition-all duration-300 group ${
                  selectedIds.includes(comment._id)
                    ? "border-yellow-300 bg-yellow-50/30"
                    : "border-gray-100"
                }`}
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(comment._id)}
                      onChange={() => toggleSelect(comment._id)}
                      className="mt-1 w-4 h-4 accent-yellow-500 cursor-pointer"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-7 h-7 bg-gradient-to-br from-gray-800 to-gray-950 rounded-lg flex items-center justify-center text-yellow-500 text-[10px] font-black group-hover:from-yellow-500 group-hover:to-yellow-600 group-hover:text-white transition-all duration-500 shadow-sm">
                          {comment.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="flex flex-col truncate">
                          <h3 className="font-black text-gray-900 text-[13px] uppercase tracking-tight leading-none truncate">
                            {comment.name}
                          </h3>
                          <span className="text-[9px] text-gray-400 font-bold mt-1 uppercase tracking-widest hidden sm:block">
                            Commenter
                          </span>
                        </div>
                      </div>

                      <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 mb-3">
                        <p className="text-gray-700 text-[13px] leading-relaxed font-medium italic">
                          "{comment.text}"
                        </p>
                      </div>

                      <div className="flex items-center gap-3">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">
                          In{" "}
                          <span className="text-yellow-600">
                            {comment.blogId?.title?.split(" ")[0] || "Untitled"}
                          </span>
                        </p>
                        <span className="w-1 h-1 bg-gray-200 rounded-full" />
                        <p className="text-[10px] text-gray-300 font-bold uppercase tracking-tighter">
                          {new Date(comment.createdAt).toLocaleDateString(
                            undefined,
                            { month: "short", day: "numeric", year: "numeric" },
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleDelete(comment._id)}
                    className="p-1.5 rounded-lg bg-gray-50 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all flex-shrink-0"
                    title="Remove Diskantly"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {maxPage > 1 && (
            <div className="flex justify-center items-center gap-3 pb-4">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="p-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="text-sm font-semibold text-gray-700 bg-white border border-gray-100 px-4 py-2 rounded-xl">
                Page {page} of {maxPage}
              </span>
              <button
                disabled={page === maxPage}
                onClick={() => setPage((p) => p + 1)}
                className="p-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Comments;
